import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, AlertTriangle, FileWarning, FileText, ClipboardList, CheckCheck, Gavel, BookOpen } from "lucide-react";

const ICONS = { low_stock: AlertTriangle, doc_expiry: FileWarning, quote_approval: FileText, po_approval: ClipboardList, tender_closing: Gavel, sop_ack: BookOpen };
const SEV = { danger: "text-primary", warning: "text-amber-500", info: "text-blue-500" };

export function NotificationBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/notifications").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    const ids = data.items.filter((n) => !n.read).map((n) => n.id);
    if (!ids.length) return;
    await api.post("/notifications/read", { ids });
    load();
  };

  const openItem = async (n) => {
    if (!n.read) await api.post("/notifications/read", { ids: [n.id] });
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-10 w-10" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-count">
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-display font-semibold">Notifications</span>
          <button onClick={markAll} className="text-xs text-primary font-medium flex items-center gap-1 hover:underline" data-testid="mark-all-read">
            <CheckCheck className="h-3.5 w-3.5" /> Mark all read
          </button>
        </div>
        <ScrollArea className="max-h-96">
          {data.items.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">You're all caught up.</div>
          ) : (
            data.items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <button
                  key={n.id} onClick={() => openItem(n)} data-testid="notification-item"
                  className={`w-full text-left flex gap-3 px-4 py-3 border-b border-border hover:bg-secondary transition-colors ${n.read ? "opacity-60" : ""}`}
                >
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${SEV[n.severity] || "text-muted-foreground"}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{n.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{n.message}</div>
                  </div>
                  {!n.read && <span className="ml-auto mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />}
                </button>
              );
            })
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
